import { EApplicantType, ECreditApplicationStatus, EDecision, EDocumentStatus, EFinancingProgram } from '@if/core/types'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import { Banner, DataField, EnumBanner, SubsectionTitle } from '@if/mui'
import { Grid, Link, Stack } from '@mui/material'
import { getIdentityDocumentTypes, getIncomeVerificationDocumentsTypes } from '@src/data/income-selectors'
import { ApplicantDto } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { useTranslation } from 'react-i18next'
import { ECreditApplicationStatusMessage, GetIncomeVerificationStatus } from '../../viewCreditApp-selectors'
import { useBankAccountProcedureDialog } from '../IncomeVerificationSection/BankAccountProcedureDialog'
import IncomeVerificationMismatch from '../IncomeVerificationSection/IncomeVerificationMismatch'
import NameMismatch from '../IncomeVerificationSection/NameMismatch'
import StartIncomeVerification from '../IncomeVerificationSection/StartIncomeVerification'
import {
  getVerifiedIncome,
  useHasBankStatementOrVoidCheque,
  useHasRequiredDocuments,
} from '../IncomeVerificationSection/incomeVerification-hooks'

interface Props {
  applicant: ApplicantDto
  requiredDocuments: RequiredDocument[]
  creditDecision: EDecision
  applicationStatus: ECreditApplicationStatus
  creditApplicationId: string
  merchant: Merchant
  financingProgramId: EFinancingProgram
  canEdit: boolean
}

// One applicant's income verification: name + (optional) status banner + amounts + reused flow components.
const IncomeApplicantBlock = ({
  applicant,
  requiredDocuments,
  creditDecision,
  applicationStatus,
  creditApplicationId,
  merchant,
  financingProgramId,
  canEdit,
}: Props) => {
  const { t } = useTranslation()
  const applicantType = applicant.isPrimaryApplicant ? EApplicantType.Applicant : EApplicantType.CoApplicant
  const procedureDialog = useBankAccountProcedureDialog({
    applicantType,
    creditApplicationId,
    canSkipFlinks: merchant.canSkipFlinks,
    financingProgramId,
  })
  const canStartStep = creditDecision === EDecision.Approved
  const isDone = applicant.incomeVerification?.allIncomeTasksConfirmed ?? false
  const showVerifiedIncome =
    isDone || (applicant.incomeVerification?.incomeMismatch && !applicant.incomeVerification.nameMismatch)
  const hasIncomeServiceAdjustment = applicant.incomeVerification?.hasIncomeServiceAdjustment ?? false
  const hasBankStatementOrVoidCheque = useHasBankStatementOrVoidCheque(requiredDocuments, applicantType)

  const hasIncomeDocumentsPendingUpload = useHasRequiredDocuments(
    requiredDocuments,
    getIncomeVerificationDocumentsTypes,
    applicantType,
    EDocumentStatus.AwaitingDocument,
  )
  const hasIncomeDocumentsPendingReview = useHasRequiredDocuments(
    requiredDocuments,
    getIncomeVerificationDocumentsTypes,
    applicantType,
    EDocumentStatus.AwaitingApproval,
  )
  const hasIncomeDocumentsApproved = useHasRequiredDocuments(
    requiredDocuments,
    getIncomeVerificationDocumentsTypes,
    applicantType,
    EDocumentStatus.Approved,
  )
  const hasIdentityDocumentPendingUpload = useHasRequiredDocuments(
    requiredDocuments,
    getIdentityDocumentTypes,
    applicantType,
    EDocumentStatus.AwaitingDocument,
  )
  const hasIdentityDocumentPendingReview = useHasRequiredDocuments(
    requiredDocuments,
    getIdentityDocumentTypes,
    applicantType,
    EDocumentStatus.AwaitingApproval,
  )
  const hasIdentityDocumentApproved = useHasRequiredDocuments(
    requiredDocuments,
    getIdentityDocumentTypes,
    applicantType,
    EDocumentStatus.Approved,
  )

  const iconMessage = GetIncomeVerificationStatus(
    applicant,
    canStartStep,
    hasIncomeDocumentsPendingReview,
    hasIncomeDocumentsPendingUpload,
    hasIncomeDocumentsApproved,
    isDone,
    applicationStatus,
    hasIdentityDocumentPendingReview,
    hasIdentityDocumentApproved,
  )

  // The bank-account procedure help was reachable while the step is in progress.
  const canOpenProcedure = iconMessage !== null && !isDone && applicationStatus !== ECreditApplicationStatus.Cancelled

  const isCancelled = !isDone && applicationStatus === ECreditApplicationStatus.Cancelled
  const hasFraudAlert = applicant.incomeVerification?.hasPendingFraudAlert ?? false
  const isWaitingForFlinks =
    (applicant.incomeVerification?.thirdPartyFlowCompleted ?? false) && !hasBankStatementOrVoidCheque
  // Interactive flow only runs once the step is startable and not in a terminal message state.
  const isInteractive = canStartStep && !isCancelled && !hasFraudAlert

  // The single per-applicant banner's description text for the current state.
  const bannerMessageKey = isCancelled
    ? ('viewCreditApplication.creditAppCancelled' as const)
    : hasFraudAlert
      ? ('incomeVerification.startVerification.reviewing' as const)
      : !canStartStep
        ? ('incomeVerification.notStarted' as const)
        : isDone
          ? ('incomeVerification.incomeVerified' as const)
          : isWaitingForFlinks
            ? ('incomeVerification.message.waitingForFlinks' as const)
            : undefined

  // One banner per applicant: status color/label from the registry, state message as its description.
  const banner =
    iconMessage !== null ? (
      <EnumBanner set={ECreditApplicationStatusMessage} value={iconMessage}>
        {bannerMessageKey ? t(bannerMessageKey) : undefined}
      </EnumBanner>
    ) : bannerMessageKey ? (
      <Banner severity="info">{t(bannerMessageKey)}</Banner>
    ) : null

  return (
    <Stack spacing={1.5}>
      <SubsectionTitle>{`${applicant.firstName} ${applicant.lastName} (${t(`common.${applicantType}`)})`}</SubsectionTitle>
      {banner}
      {isInteractive && (
        <>
          {/* Verification not yet started by the client */}
          {!applicant.incomeVerification?.thirdPartyFlowCompleted && !isDone && (
            <StartIncomeVerification
              applicant={applicant}
              hasIncomeDocumentsPendingUpload={hasIncomeDocumentsPendingUpload}
              hasIncomeDocumentsPendingReview={hasIncomeDocumentsPendingReview}
              hasIncomeDocumentsApproved={hasIncomeDocumentsApproved}
              creditApplicationId={creditApplicationId}
              financingProgramId={financingProgramId}
              canSkipFlinks={merchant.canSkipFlinks}
              canEdit={canEdit}
            />
          )}
          {/* Name mismatch resolution */}
          {applicant.incomeVerification?.nameMismatch && (
            <NameMismatch
              financingProgramId={financingProgramId}
              hasIdentityDocumentsPendingReview={hasIdentityDocumentPendingReview}
              hasIdentityDocumentsPendingUpload={hasIdentityDocumentPendingUpload}
              allIncomesConfirmed={isDone}
              applicantType={applicantType}
              creditApplicationId={creditApplicationId}
              canEdit={canEdit}
            />
          )}
          {/* Income mismatch resolution */}
          {applicant.incomeVerification?.incomeMismatch && !applicant.incomeVerification.nameMismatch && !isDone && (
            <IncomeVerificationMismatch
              financingProgramId={financingProgramId}
              applicant={applicant}
              hasIncomeDocumentsPendingReview={hasIncomeDocumentsPendingReview}
              hasIncomeDocumentsPendingUpload={hasIncomeDocumentsPendingUpload}
              hasIncomeServiceAdjustment={hasIncomeServiceAdjustment}
              creditApplicationId={creditApplicationId}
              canEdit={canEdit}
            />
          )}
          {/* Verified / declared amounts (staff only) */}
          {canEdit && (
            <Grid container spacing={2}>
              {showVerifiedIncome && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DataField
                    label={t('incomeVerification.verifiedIncome')}
                    value={formatCurrencyNoDecimals(
                      getVerifiedIncome(hasBankStatementOrVoidCheque, hasIncomeDocumentsApproved, applicant),
                    )}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <DataField
                  label={t('incomeVerification.totalIncome')}
                  value={
                    applicant.incomeVerification
                      ? formatCurrencyNoDecimals(applicant.incomeVerification.declaredIncome)
                      : undefined
                  }
                />
              </Grid>
            </Grid>
          )}
        </>
      )}
      {canOpenProcedure && (
        <Link
          component="button"
          type="button"
          onClick={() => void procedureDialog.open()}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('incomeVerification.bankAccountProcedure.whatsFlinks')}
        </Link>
      )}
    </Stack>
  )
}

export default IncomeApplicantBlock
